import React, { useState } from 'react';
import './TemplateRequestModal.css';

interface TemplateRequestModalProps {
  templateTitle: string;
  templateId: string;
  onClose: () => void;
  onRequest: (templateId: string, email: string) => Promise<boolean>;
}

const TemplateRequestModal: React.FC<TemplateRequestModalProps> = ({
  templateTitle,
  templateId,
  onClose,
  onRequest
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const success = await onRequest(templateId, email);
      if (success) {
        onClose();
      }
    } catch (err) {
      setError('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="template-request-modal-backdrop" onClick={handleBackdropClick}>
      <div className="template-request-modal">
        <div className="modal-header">
          <h3>Request Template: {templateTitle}</h3>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          <p>
            Enter your email address below and we'll send you the <strong>{templateTitle}</strong> template
            with detailed examples, best practices, and implementation guides.
          </p>

          <form onSubmit={handleSubmit} className="template-request-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={loading}
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Sending Request...' : 'Send Me The Template'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>

          <div className="modal-footer-note">
            <p>
              📧 We'll send the template to your email within minutes. 
              Your email will only be used for template delivery and you can unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateRequestModal;